import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Rnd } from "react-rnd";
import { ArrowLeft, ListTodo, Tag } from "lucide-react";
import TasksPanel from "./TasksPanel";
import { ResizeGrip } from "./ResizeGrip";
import { nextWindowZ, isInsideTaskPanel, setPanelDropHint, pointer } from "./TaskGroupViews";
import {
  getDetachedPanels,
  subscribeDetachedPanels,
  closeDetachedPanel,
  loadPanelGeom as loadGeom,
  savePanelGeom as saveGeom,
} from "../lib/detachedPanels";

const PanelWindow = ({ panel, index, onData }) => {
  const [z, setZ] = useState(() => 60 + index);
  // FAZ C — kaydedilen z-sırasını geri yükle (kategori penceresi).
  useEffect(() => {
    const onLayoutZ = (ev) => {
      const v = ev?.detail?.[`category:${panel.category}`];
      if (v != null) setZ(v);
    };
    window.addEventListener("sertex:layout-z", onLayoutZ);
    return () => window.removeEventListener("sertex:layout-z", onLayoutZ);
  }, [panel.category]);
  const g =
    loadGeom(panel.id) || {
      x: 140 + index * 40,
      y: 90 + index * 40,
      width: 460,
      height: 620,
    };
  return createPortal(
    <Rnd
      default={g}
      minWidth={340}
      minHeight={300}
      bounds="window"
      dragHandleClassName="dp-drag"
      onMouseDown={() => setZ(nextWindowZ())}
      onDrag={() => setPanelDropHint(isInsideTaskPanel(pointer.x, pointer.y))}
      onDragStop={(e, d) => {
        setPanelDropHint(false);
        // AŞAMA 12 (Adım 3) — pencere Neural Link panelinin üzerine bırakıldıysa
        // kol listeye geri döner (ebeveyn/kardeş indeksi veri modelinde
        // değişmediği için eski yerine birebir oturur).
        if (isInsideTaskPanel(pointer.x, pointer.y)) { closeDetachedPanel(panel.id); return; }
        saveGeom(panel.id, {
          x: d.x,
          y: d.y,
          width: loadGeom(panel.id)?.width || g.width,
          height: loadGeom(panel.id)?.height || g.height,
        });
      }}
      onResizeStop={(e, dir, ref, delta, pos) =>
        saveGeom(panel.id, {
          x: pos.x,
          y: pos.y,
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        })
      }
      style={{ zIndex: z }}
      resizeHandleComponent={{ bottomRight: <ResizeGrip testId={`panel-${panel.id}`} /> }}
      data-testid={`detached-panel-window-${panel.id}`}
    >
      {/* overflow-hidden iç sarmalayıcıda — Rnd kökünde OLMAMALI, yoksa
          react-rnd'nin kutu dışına taşan resize tutamaçları kırpılır ve
          boyutlandırma çalışmaz (DetachedTaskWindow ile aynı yapı). */}
      <div className="h-full flex flex-col glass-panel corner-bracket border border-sertex-cyan/40 shadow-2xl overflow-hidden bg-sertex-bg/95 rounded-lg">
        <div className="dp-drag cursor-move flex items-center justify-between px-3 py-2 border-b border-sertex-cyan/30 bg-sertex-surface/70 shrink-0">
          <div className="hud-text text-sertex-cyan flex items-center gap-1.5 truncate">
            {panel.category ? (
              <Tag className="h-3 w-3 shrink-0" />
            ) : (
              <ListTodo className="h-3 w-3 shrink-0" />
            )}
            <span className="truncate">{panel.categoryName || "GÖREVLER"}</span>
          </div>
          <button
            onClick={() => closeDetachedPanel(panel.id)}
            data-testid={`detached-panel-dock-${panel.id}`}
            title="Sidebar'a geri al"
            className="p-1 rounded border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15 transition-colors shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-sertex p-3">
          <TasksPanel
            detached
            initialCategory={panel.category}
            onDataChanged={onData}
            onDock={() => closeDetachedPanel(panel.id)}
          />
        </div>
      </div>
    </Rnd>,
    document.body,
  );
};

export const DetachedPanelsHost = ({ onDataChanged }) => {
  const [panels, setPanels] = useState(getDetachedPanels());
  useEffect(() => subscribeDetachedPanels(setPanels), []);
  if (!panels.length) return null;
  return (
    <>
      {panels.map((p, i) => (
        <PanelWindow key={p.id} panel={p} index={i} onData={onDataChanged} />
      ))}
    </>
  );
};

export default DetachedPanelsHost;
